import { Descriptions, Typography, Skeleton, Tabs, Space, Divider } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { shortenString } from "@lib/helpers/StringHelper";
import dayjs from "dayjs";
import { XAICall, XAICallLog } from "@lib/types/xAI";
import { AppJson } from "@lib/components/AppComponents";
import CoinPriceTextFormat from "@lib/components/MiniComponents/Others/CoinPriceTextFormat";
import { useState } from "react";
import {
  StyledDescriptions,
  StyledDrawer,
  ConversationAnalysis,
  SummarySection,
  StyledXAICallFooter,
  StyledXAICallBody,
  StyledCollapse,
} from "./index.styled";
import VoicePlayer from "@lib/components/MiniComponents/Voice";
import { useQuery } from "@tanstack/react-query";
import { apiGetXAICall } from "@lib/api/xAI";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import {
  CustomerName,
  XAICallStatusTag,
  XAIClientMoodTag,
  XAIDisconnectionResultTag,
  AppointmentTime,
  CallDuration,
  Keywords,
} from "@lib/components/MiniComponents/Others";

type Props = {
  open: boolean;
  onClose: () => void;
  item: XAICall | null;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 9 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const XAICallDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { messages } = useIntl();
  const [activeTab, setActiveTab] = useState("transcription");

  const callsQuery = useQuery({
    queryKey: ["xai-call", item?.id],
    queryFn: () =>
      apiGetXAICall(item?.id, { join: ["histories", "lead", "customer"] }),
    enabled: !!item?.id && open,
  });
  const isLoading = callsQuery?.isLoading || callsQuery?.isFetching;
  const callData = callsQuery?.data?.data;

  const callDate =
    callData?.createdAt || item?.createdAt
      ? dayjs(callData?.createdAt || item?.createdAt).format("MM/DD/YYYY HH:mm")
      : "-";
  const callCost = callData?.callCost ? Number(callData?.callCost) : 0;
  const callId = callData?.callId || item?.id || "-";
  const agentName = callData?.agentName || "-";
  const clientMood = callData?.clientMood || "-";
  const callStatus = callData?.callStatus || callData?.status || "-";
  const disconnectionReason = callData?.disconnectionResult || "-";
  const summary = callData?.summary || "-";
  const description = callData?.description || callData?.transcript || "-";
  const voiceFile = callData?.voiceFile || "";
  const histories = callData?.histories || [];
  const durationMs = callData?.durationMs || null;
  const keywords = callData?.keywords || item?.keywords || null;

  const renderTranscription = (transcriptText?: string | null) => {
    const text = transcriptText || description;
    if (!text || text === "-") {
      return (
        <Typography.Text type="secondary">
          <FormattedMessage id="common.noTranscriptionAvailable" />
        </Typography.Text>
      );
    }
    return (
      <div>
        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}>{text}</div>
      </div>
    );
  };

  const renderData = (dataToShow?: any) => {
    const dataSource = dataToShow || callData;
    if (!dataSource) {
      return (
        <Typography.Text type="secondary">
          <FormattedMessage id="common.noDataAvailable" />
        </Typography.Text>
      );
    }
    return <AppJson src={dataSource} collapsed={true} />;
  };

  const renderHistoryItem = (history: XAICallLog, index: number) => {
    const historyDate = history?.createdAt
      ? dayjs(history?.createdAt).format("MM/DD/YYYY HH:mm:ss")
      : `Log ${index + 1}`;

    const historyDescription = history?.rawCallback?.description || "-";
    const historySummary = history?.rawCallback?.summary || "-";
    const historyCallStatus =
      history?.rawCallback?.callStatus || history?.status || "-";
    const historyDisconnectionReason =
      history?.rawCallback?.disconnectionReasult || "-";
    const historyVoiceFile = history?.rawCallback?.VoiceFile || "";

    return (
      <div style={{ padding: "8px 0" }}>
        {/* History Information */}
        <StyledDescriptions
          bordered
          column={1}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label={<FormattedMessage id="common.date" />}>
            <Typography.Text>{historyDate}</Typography.Text>
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.appointmentTime" />}
          >
            <AppointmentTime appointmentTime={history?.appointmentTime} />
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.callStatus" />}
          >
            <XAICallStatusTag status={historyCallStatus} size="small" />
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.userSentiment" />}
          >
            <XAIClientMoodTag
              mood={history?.rawCallback?.ClientMood}
              size="small"
            />
          </Descriptions.Item>

          <Descriptions.Item
            label={<FormattedMessage id="common.disconnectionReason" />}
          >
            <XAIDisconnectionResultTag
              result={historyDisconnectionReason}
              size="small"
            />
          </Descriptions.Item>

          <Descriptions.Item label={<FormattedMessage id="common.duration" />}>
            <CallDuration durationMs={durationMs} size="small" />
          </Descriptions.Item>
        </StyledDescriptions>

        {/* History Audio */}
        {historyVoiceFile && (
          <>
            <VoicePlayer
              voiceFile={historyVoiceFile}
              containerStyle={{ margin: "16px 0", padding: "16px" }}
            />
            <Divider />
          </>
        )}

        <>
          <SummarySection>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              <FormattedMessage id="common.summary" />
            </Typography.Title>
            <Typography.Text>{historySummary}</Typography.Text>
          </SummarySection>
          <Divider />
        </>

        <>
          <div style={{ marginTop: 16 }}>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              <FormattedMessage id="common.transcription" />
            </Typography.Title>
            {renderTranscription(historyDescription)}
          </div>
          <Divider />
        </>

        {/* History Raw Data */}
        <div style={{ marginTop: 16 }}>
          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            <FormattedMessage id="common.data" />
          </Typography.Title>
          {renderData(history?.rawCallback)}
        </div>
      </div>
    );
  };

  const renderLogsTab = () => {
    if (!histories || histories.length === 0) {
      return (
        <Typography.Text type="secondary">
          <FormattedMessage id="common.noDataAvailable" />
        </Typography.Text>
      );
    }

    const collapseItems = histories.map((history, index) => {
      const status = history?.rawCallback?.callStatus || history?.status || "-";

      return {
        key: history?.id || `log-${index}`,
        label: (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Typography.Text strong>
                {history?.createdAt
                  ? dayjs(history?.createdAt).format("MM/DD/YYYY HH:mm:ss")
                  : `Log ${index + 1}`}
              </Typography.Text>

              <XAICallStatusTag status={status} size="small" />

              <XAIClientMoodTag
                mood={history?.rawCallback?.ClientMood}
                size="small"
              />
            </div>

            <CallDuration durationMs={durationMs} size="small" />
          </div>
        ),
        children: renderHistoryItem(history, index),
      };
    });

    return (
      <StyledCollapse
        items={collapseItems}
        defaultActiveKey={collapseItems.map((item) => item.key)}
        style={{ background: "transparent" }}
      />
    );
  };

  const tabItems = [
    {
      key: "transcription",
      label: messages["common.transcription"] as string,
      children: renderTranscription(),
    },
    {
      key: "data",
      label: messages["common.data"] as string,
      children: renderData(),
    },
    ...(histories && histories.length > 0
      ? [
          {
            key: "logs",
            label: `${messages["common.logs"] as string} (${histories.length})`,
            children: renderLogsTab(),
          },
        ]
      : []),
  ];

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={"common.previewTitle"}
          values={{ title: messages["common.xaiCall"] as string }}
        />
      }
      onClose={onClose}
      open={open}
      width={800}
      id="xai-call-drawer"
      extra={
        <ActionButton
          type="refresh"
          size="small"
          query={callsQuery}
          onClick={() => callsQuery?.refetch()}
        />
      }
    >
      {isLoading ? (
        <SkeletonContent />
      ) : (
        <>
          <StyledXAICallBody>
            {/* Call Information */}
            <StyledDescriptions bordered column={1} size="small">
              <Descriptions.Item
                label={<FormattedMessage id="common.callId" />}
              >
                <Typography.Text copyable={{ text: callId }}>
                  {callId}
                </Typography.Text>
              </Descriptions.Item>

              <Descriptions.Item label={<FormattedMessage id="common.name" />}>
                {callData?.lead?.firstName} {callData?.lead?.lastName}
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.customer" />}
              >
                <CustomerName customer={callData?.customer} link />
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.phoneCallType" />}
              >
                {callDate}
              </Descriptions.Item>

              <Descriptions.Item label={<FormattedMessage id="common.agent" />}>
                <Typography.Text>
                  <FormattedMessage id="common.agent" />: {agentName}
                  {callData?.id && (
                    <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                      [{shortenString(callData?.id, 20)}]
                    </Typography.Text>
                  )}
                </Typography.Text>
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.duration" />}
              >
                <CallDuration durationMs={durationMs} size="small" />
              </Descriptions.Item>

              <Descriptions.Item
                label={<FormattedMessage id="common.callCost" />}
              >
                <CoinPriceTextFormat
                  amount={callCost}
                  showCurrencySymbol
                  align="left"
                />
              </Descriptions.Item>
            </StyledDescriptions>

            <Divider />

            {/* Audio Player */}
            <>
              <VoicePlayer
                voiceFile={voiceFile}
                containerStyle={{ margin: "16px 0", padding: "16px" }}
              />
              <Divider />
            </>

            {/* Conversation Analysis */}
            <ConversationAnalysis>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Typography.Title level={5} style={{ margin: 0 }}>
                  <FormattedMessage id="common.conversationAnalysis" />
                </Typography.Title>
              </div>
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="small"
              >
                {/* <div>
                  <Typography.Text strong>
                    <FormattedMessage id="common.callSuccessful" />:{" "}
                  </Typography.Text>
                  <Tag color={callStatus === "ended" ? "red" : "green"}>
                    {callStatus === "ended" ? (
                      <FormattedMessage id="common.unsuccessful" />
                    ) : (
                      <FormattedMessage id="common.successful" />
                    )}
                  </Tag>
                </div> */}
                <div>
                  <Typography.Text strong>
                    <FormattedMessage id="common.callStatus" />:{" "}
                  </Typography.Text>
                  <XAICallStatusTag status={callStatus} />
                </div>
                <div>
                  <Typography.Text strong>
                    <FormattedMessage id="common.userSentiment" />:{" "}
                  </Typography.Text>
                  <XAIClientMoodTag mood={clientMood} />
                </div>
                <div>
                  <Typography.Text strong>
                    <FormattedMessage id="common.disconnectionReason" />:{" "}
                  </Typography.Text>
                  <XAIDisconnectionResultTag result={disconnectionReason} />
                </div>
                <div>
                  <Typography.Text strong>
                    <FormattedMessage id="common.endToEndLatency" />:{" "}
                  </Typography.Text>
                  <CallDuration durationMs={durationMs} size="small" />
                </div>
                {keywords && keywords.length > 0 && (
                  <div>
                    <Typography.Text strong>
                      <FormattedMessage id="common.keywords" />:{" "}
                    </Typography.Text>
                    <Keywords keywords={keywords} wrap />
                  </div>
                )}
              </Space>
            </ConversationAnalysis>

            <Divider />

            {/* Summary */}
            {summary && summary !== "-" && (
              <>
                <SummarySection>
                  <Typography.Title level={5} style={{ marginBottom: 12 }}>
                    <FormattedMessage id="common.summary" />
                  </Typography.Title>
                  <Typography.Text>{summary}</Typography.Text>
                </SummarySection>
                <Divider />
              </>
            )}
          </StyledXAICallBody>

          {/* Tabs */}
          <StyledXAICallFooter>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
            />
          </StyledXAICallFooter>
        </>
      )}
    </StyledDrawer>
  );
};
